import React, { useEffect, useRef, useState } from "react";
import { Grid, TextField, Autocomplete, Checkbox, FormControlLabel } from "@mui/material";
import countriesData from "../../data/countries";
import { fetchPincodeData } from "../../services/pincodeService";

const AddressFields = ({
  formData,
  handleChange,
  addressType = "Current", // "Current" or "Permanent"
  section = 1,
  textInputStyle = {},
  fieldErrors = {},
  onSameAsCurrentChange, // Callback when "Same as Current Address" is checked
  isSameAsCurrent = false, // Prop to indicate if permanent address is same as current
}) => {
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [talukOptions, setTalukOptions] = useState([]);
  const [villageOptions, setVillageOptions] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [countryOptions] = useState(countriesData.map((c) => c.name));

  const previousCountryRef = useRef();
  const previousStateRef = useRef();
  const previousDistrictRef = useRef();

  // Field name prefix based on address type
  const prefix = addressType === "Current" ? "current" : "permanent";

  // Get field values
  const country = formData[`${prefix}_country${section}`] || "";
  const state = formData[`${prefix}_state${section}`] || "";
  const district = formData[`${prefix}_district${section}`] || "";
  const taluk = formData[`${prefix}_taluk${section}`] || "";
  const village = formData[`${prefix}_village${section}`] || "";
  const pincode = formData[`${prefix}_pincode${section}`] || "";
  const address = formData[`${prefix}_address${section}`] || "";
  const street = formData[`${prefix}_street${section}`] || "";


  // Field errors
  const addressError = fieldErrors[`${prefix}_address${section}`] || "";
  const streetError = fieldErrors[`${prefix}_street${section}`] || "";
  const pincodeError = fieldErrors[`${prefix}_pincode${section}`] || "";
  const villageError = fieldErrors[`${prefix}_village${section}`] || "";
  const talukError = fieldErrors[`${prefix}_taluk${section}`] || "";
  const districtError = fieldErrors[`${prefix}_district${section}`] || "";
  const stateError = fieldErrors[`${prefix}_state${section}`] || "";
  const countryError = fieldErrors[`${prefix}_country${section}`] || "";

  // Handle pincode selection and auto-fill
  const handlePincodeChange = async (newPincode) => {
    handleChange({ target: { name: `${prefix}_pincode${section}`, value: newPincode } });

    if (!newPincode || newPincode.trim().length < 3) {
      return;
    }

    // Get country code
    let countryCode = "IN";
    const selectedCountry = countriesData.find((c) => c.name === country);
    if (selectedCountry && selectedCountry.code) {
      countryCode = selectedCountry.code.toUpperCase();
    }

    const result = await fetchPincodeData(newPincode, countryCode);
    if (result && result.success) {
      // Set village options from allAreas if available
      if (result.allAreas && result.allAreas.length > 0) {
        setVillageOptions(result.allAreas);
        // Don't auto-fill village, let user select from dropdown
        // Clear the village field to show the dropdown
        handleChange({ target: { name: `${prefix}_village${section}`, value: "" } });
      } else if (result.village) {
        // If no areas but single village, auto-fill it
        setVillageOptions([result.village]);
        handleChange({ target: { name: `${prefix}_village${section}`, value: result.village } });
      }
      
      // Auto-fill other fields from pincode data
      if (result.taluk) {
        handleChange({ target: { name: `${prefix}_taluk${section}`, value: result.taluk } });
      }
      if (result.district) {
        handleChange({ target: { name: `${prefix}_district${section}`, value: result.district } });
      }
      if (result.state) {
        handleChange({ target: { name: `${prefix}_state${section}`, value: result.state } });
      }
      if (result.country) {
        handleChange({ target: { name: `${prefix}_country${section}`, value: result.country } });
      }
    }
  };

  // Load states based on country (placeholder - implement if needed)
  useEffect(() => {
    if (country) {
      // For India, you can add states list here
      if (country === "India") {
        setStateOptions([
          "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
          "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
          "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
          "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
          "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
          "Uttar Pradesh", "Uttarakhand", "West Bengal",
          "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
          "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
        ]);
      } else {
        setStateOptions([]);
      }
    } else {
      setStateOptions([]);
    }
  }, [country]);

  return (
    <Grid container spacing={3}>
      {/* Address (Textarea) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {addressType} Address
        </label>
        <TextField
          fullWidth
          multiline
          rows={3}
          name={`${prefix}_address${section}`}
          value={address}
          onChange={handleChange}
          variant="outlined"
          placeholder={`Enter ${addressType.toLowerCase()} address`}
          sx={textInputStyle}
          error={!!addressError}
          helperText={addressError}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* Street (Textarea) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Street
        </label>
        <TextField
          fullWidth
          multiline
          rows={3}
          name={`${prefix}_street${section}`}
          value={street}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter street"
          sx={textInputStyle}
          error={!!streetError}
          helperText={streetError}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* Pincode (Dropdown with auto-fill) */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pincode
        </label>
        <Autocomplete
          freeSolo
          options={pincodeOptions}
          value={pincode}
          onChange={(e, value) => handlePincodeChange(value || "")}
          onInputChange={(e, value) => {
            if (e && e.type === 'change') {
              handlePincodeChange(value || "");
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter pincode"
              sx={textInputStyle}
              error={!!pincodeError}
              helperText={pincodeError || "Pincode will auto-fill other fields"}
            />
          )}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* Village/Town */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Village/Town
        </label>
        <Autocomplete
          freeSolo
          options={villageOptions}
          value={village}
          onChange={(e, value) =>
            handleChange({ target: { name: `${prefix}_village${section}`, value: value || "" } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={villageOptions.length > 0 ? "Select village/town from list" : "Enter village/town"}
              sx={textInputStyle}
              error={!!villageError}
              helperText={villageError || (villageOptions.length > 0 ? `${villageOptions.length} area(s) available for this pincode` : "")}
            />
          )}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* Taluk */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Taluk
        </label>
        <Autocomplete
          freeSolo
          options={talukOptions}
          value={taluk}
          onChange={(e, value) =>
            handleChange({ target: { name: `${prefix}_taluk${section}`, value: value || "" } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select taluk"
              sx={textInputStyle}
              error={!!talukError}
              helperText={talukError}
            />
          )}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* District */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          District
        </label>
        <Autocomplete
          freeSolo
          options={districtOptions}
          value={district}
          onChange={(e, value) =>
            handleChange({ target: { name: `${prefix}_district${section}`, value: value || "" } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select district"
              sx={textInputStyle}
              error={!!districtError}
              helperText={districtError}
            />
          )}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* State */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          State
        </label>
        <Autocomplete
          freeSolo
          options={stateOptions}
          value={state}
          onChange={(e, value) =>
            handleChange({ target: { name: `${prefix}_state${section}`, value: value || "" } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select state"
              sx={textInputStyle}
              error={!!stateError}
              helperText={stateError}
            />
          )}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>

      {/* Country */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Country
        </label>
        <Autocomplete
          freeSolo
          options={countryOptions}
          value={country}
          onChange={(e, value) =>
            handleChange({ target: { name: `${prefix}_country${section}`, value: value || "" } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select country"
              sx={textInputStyle}
              error={!!countryError}
              helperText={countryError}
            />
          )}
          disabled={isSameAsCurrent && addressType === "Permanent"}
        />
      </Grid>
    </Grid>
  );
};

export default AddressFields;
